import type { Folder, ZimbraClient } from '@zimbra-webif/sdk';

/**
 * House-cleaning: user rules that move matching mail out of chosen folders
 * (Inbox by default) into "HouseCleaning/<source folder path>", so cleaned mail
 * stays sorted by where it came from (Inbox → HouseCleaning/Inbox,
 * ap@mitral.dk → HouseCleaning/ap@mitral.dk). A rule is a set of conditions compiled to one Zimbra
 * search query (syntax verified against the live server 2026-10-02); nothing is
 * ever deleted. Rules live in mailbox metadata so they follow the user across
 * devices.
 */

export type ReadState = 'any' | 'read' | 'unread';
export type AttachState = 'any' | 'yes' | 'no';

export interface CleanRule {
  id: string;
  name: string;
  enabled: boolean;
  /** source folder ids to clean (missing = Inbox only, as before folders existed) */
  folders?: string[];
  /** who: a brand/word ("paypal" = all PayPal), a domain ("paypal.dk") or an address */
  senders: string[];
  /** subject contains */
  subject?: string;
  /** only mail older than N days (0 / undefined = any age) */
  olderDays?: number;
  read?: ReadState;
  attach?: AttachState;
  /** only mail larger than N MB */
  largerMb?: number;
  /** extra raw Zimbra search text, ANDed in */
  query?: string;
  /** a sample sender address, only used to show the sender's logo */
  logo?: string;
}

export interface CleanSettings {
  rules: CleanRule[];
  /** run enabled rules on open + hourly while webmail is open */
  auto: boolean;
}

export const CLEAN_FOLDER = 'HouseCleaning';
export const INBOX_ID = '2';
/** system folders a rule never cleans: Trash, Junk, Drafts */
const NEVER_SOURCE = new Set(['3', '4', '6']);
const META_SECTION = 'zwc:housecleaning';
/*
 * Load control. Zimbra serialises writes per mailbox and fails any request once
 * 15 are queued on the lock ("too many waiters: 15", seen live 2026-10-02 when a
 * run moved 500-mail batches while counts ran in parallel). So: every clean call
 * goes through ONE serial queue, moves are small batches with a pause between,
 * and a lock failure is retried with backoff instead of aborting.
 */
const BATCH = 200;
/**
 * Moving out of a share is copy here + delete in the owner's mailbox: 200 mails
 * took 56–60 s live (2026-10-15, ap@mitral.dk), past the 60 s proxy limit →
 * HTTP 504. ~0.3 s/mail, so 25 per call stays well under it.
 */
const BATCH_SHARED = 25;
const BATCH_PAUSE_MS = 400;
/** safety stop for one run, in messages */
const MAX_MOVE = 100_000;
/** a proxy gave up waiting (the move may still have finished) — wait, re-query, go on */
const GATEWAY_RETRIES = 3;
const GATEWAY_PAUSE_MS = 5000;
export function isGatewayTimeout(e: unknown): boolean {
  const status = (e as { status?: number })?.status;
  return status === 502 || status === 503 || status === 504;
}
/** counts stop at one IDS page — shown as "1,000+" */
const COUNT_CAP = 1000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
/** one queue per client = per mailbox session (the server runs several users' cleans at once) */
const chains = new WeakMap<object, Promise<unknown>>();
/** run `fn` after every earlier clean call on this client has finished (one at a time) */
function serial<T>(client: object, fn: () => Promise<T>): Promise<T> {
  const next = (chains.get(client) ?? Promise.resolve()).then(fn, fn);
  chains.set(client, next.catch(() => undefined));
  return next;
}
export function isLockBusy(e: unknown): boolean {
  const code = (e as { code?: string })?.code ?? '';
  const msg = e instanceof Error ? e.message : String(e);
  return code === 'service.LOCK_FAILED' || /too many waiters|lock/i.test(msg);
}
/** serial + retry on a busy mailbox lock (1 s, 2 s, 4 s) */
async function call<T>(client: object, fn: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await serial(client, fn);
    } catch (e) {
      if (attempt >= 3 || !isLockBusy(e)) throw e;
      await sleep(1000 * 2 ** attempt);
    }
  }
}

/** Quote a term for a Zimbra query when it has spaces/specials. */
function q(v: string): string {
  const t = v.trim().replace(/"/g, '');
  return /^[\w@.\-+]+$/.test(t) ? t : `"${t}"`;
}

/** A sender entry that is a domain ("netflix.com" / "@netflix.com") → the domain, else null. */
export function senderDomain(s: string): string | null {
  let t = s.trim().toLowerCase();
  if (t.startsWith('@')) t = t.slice(1);
  else if (t.includes('@')) return null; // a full address
  return t.includes('.') ? t : null;
}

/**
 * One sender entry → a `from:` term.
 *
 * A domain must also match its sub-domains: Netflix mails from
 * info@members.netflix.com, never from @netflix.com. Zimbra's `from:@netflix.com`
 * matches that exact domain only and no wildcard form reaches sub-domains
 * (measured 2026-10-16: 0 hits vs 1,000+). So a domain searches by its brand word
 * (`from:netflix` — matched as an address token) and the hits are then checked
 * against the real sender address: see senderMatches / domain filtering below.
 */
export function senderTerm(s: string): string | null {
  const t = s.trim().toLowerCase();
  if (!t) return null;
  const d = senderDomain(t);
  if (d) return `from:${q(brandOf(baseDomain(d)))}`; // domain → brand word, filtered afterwards
  return `from:${q(t)}`; // full address, or a brand / word — matches name + any address part
}

/** Does the rule have domain senders (whose search hits need the address check)? */
export function ruleHasDomainSenders(r: CleanRule): boolean {
  return r.senders.some((s) => !!senderDomain(s));
}

/**
 * Does a mail's sender satisfy the rule's sender list? Only needed when the rule
 * has domain senders: a domain matches itself and every sub-domain; the other
 * entries (address, word) already match exactly through the search.
 */
export function senderMatches(r: CleanRule, addr: string, name = ''): boolean {
  const a = addr.trim().toLowerCase();
  const host = a.slice(a.lastIndexOf('@') + 1);
  const who = `${a} ${name.toLowerCase()}`;
  return r.senders.some((s) => {
    const t = s.trim().toLowerCase();
    if (!t) return false;
    const d = senderDomain(t);
    if (d) return host === d || host.endsWith(`.${d}`);
    if (t.includes('@')) return a === t;
    return who.includes(t); // a word: name or any part of the address
  });
}

export function ruleFolders(r: CleanRule): string[] {
  return r.folders ?? [INBOX_ID];
}

/**
 * Compile a rule to its Zimbra query (its source folders — or just `only` —
 * never sub-folders, never flagged mail).
 */
export function ruleQuery(r: CleanRule, only?: string): string {
  const src = only ? [only] : ruleFolders(r);
  const parts = [src.length === 1 ? `inid:${src[0]}` : `(${src.map((id) => `inid:${id}`).join(' OR ')})`, '-is:flagged'];
  const from = r.senders.map(senderTerm).filter((x): x is string => !!x);
  if (from.length === 1) parts.push(from[0]!);
  else if (from.length > 1) parts.push(`(${from.join(' OR ')})`);
  if (r.subject?.trim()) parts.push(`subject:${q(r.subject)}`);
  if (r.olderDays && r.olderDays > 0) parts.push(`before:-${Math.floor(r.olderDays)}d`);
  if (r.read === 'read') parts.push('is:read');
  else if (r.read === 'unread') parts.push('is:unread');
  if (r.attach === 'yes') parts.push('has:attachment');
  else if (r.attach === 'no') parts.push('-has:attachment');
  if (r.largerMb && r.largerMb > 0) parts.push(`larger:${r.largerMb}mb`);
  if (r.query?.trim()) parts.push(`(${r.query.trim()})`);
  return parts.join(' ');
}

/** A rule with no condition at all would match the whole Inbox — never run it. */
export function ruleIsEmpty(r: CleanRule): boolean {
  return (
    r.senders.every((s) => !s.trim()) &&
    !r.subject?.trim() &&
    !(r.olderDays && r.olderDays > 0) &&
    (r.read ?? 'any') === 'any' &&
    (r.attach ?? 'any') === 'any' &&
    !(r.largerMb && r.largerMb > 0) &&
    !r.query?.trim()
  );
}

/** Short human summary of a rule's conditions, for the rule card. */
export function ruleSummary(r: CleanRule): string[] {
  const out: string[] = [];
  const who = r.senders.filter((s) => s.trim());
  if (who.length) out.push(`from ${who.join(', ')}`);
  if (r.subject?.trim()) out.push(`subject “${r.subject.trim()}”`);
  if (r.olderDays) out.push(`older than ${ageLabel(r.olderDays)}`);
  if (r.read === 'read') out.push('read');
  if (r.read === 'unread') out.push('unread');
  if (r.attach === 'yes') out.push('with attachments');
  if (r.attach === 'no') out.push('without attachments');
  if (r.largerMb) out.push(`> ${r.largerMb} MB`);
  if (r.query?.trim()) out.push(r.query.trim());
  return out;
}

export const AGE_STEPS: Array<{ days: number; label: string }> = [
  { days: 0, label: 'Any age' },
  { days: 7, label: '1 week' },
  { days: 30, label: '1 month' },
  { days: 90, label: '3 months' },
  { days: 365, label: '1 year' },
];
export function ageLabel(days: number): string {
  return AGE_STEPS.find((s) => s.days === days)?.label.toLowerCase() ?? `${days} days`;
}

export function newRule(partial: Partial<CleanRule> = {}): CleanRule {
  return {
    id: `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    enabled: true,
    senders: [],
    olderDays: 0,
    read: 'any',
    attach: 'any',
    ...partial,
  };
}

/* ------------------------------------------------------------------ */
/* Server calls                                                        */
/* ------------------------------------------------------------------ */

export async function loadSettings(client: ZimbraClient): Promise<CleanSettings> {
  const meta = await call(client, () => client.mail.getMailboxMetadata(META_SECTION));
  const a = (meta._attrs ?? {}) as Record<string, unknown>;
  let rules: CleanRule[] = [];
  try {
    const parsed = typeof a.rules === 'string' ? (JSON.parse(a.rules) as unknown) : [];
    if (Array.isArray(parsed)) rules = parsed as CleanRule[];
  } catch {
    rules = [];
  }
  return { rules, auto: a.auto === '1' };
}

export async function saveSettings(client: ZimbraClient, s: CleanSettings): Promise<void> {
  await call(client, () => client.mail.setMailboxMetadata(META_SECTION, { rules: JSON.stringify(s.rules), auto: s.auto ? '1' : '0' }));
}

/** Message ids for a query (IDS mode). */
async function ids(client: ZimbraClient, query: string, limit: number, offset = 0): Promise<{ ids: string[]; more: boolean }> {
  const res = (await call(client, () => client.mail.search({ query, types: 'message', resultMode: 'IDS', limit, offset }))) as {
    hit?: Array<{ id: string }>;
    more?: boolean;
  };
  return { ids: (res.hit ?? []).map((h) => h.id), more: !!res.more };
}

type SearchMsg = { id: string; su?: string; d?: number; e?: Array<{ t?: string; a?: string; p?: string; d?: string }> };

/**
 * Message ids a rule matches for a query, with the sender check applied when the
 * rule has domain senders. `rejected` = hits that failed the check (they stay in
 * place, so a mover must skip past them with `offset`).
 */
async function ruleIds(
  client: ZimbraClient,
  r: CleanRule,
  query: string,
  limit: number,
  offset = 0,
): Promise<{ ids: string[]; rejected: number; more: boolean }> {
  if (!ruleHasDomainSenders(r)) {
    const page = await ids(client, query, limit, offset);
    return { ids: page.ids, rejected: 0, more: page.more };
  }
  const res = (await call(client, () => client.mail.search({ query, types: 'message', limit, offset, sortBy: 'dateDesc' }))) as {
    m?: SearchMsg[];
    more?: boolean;
  };
  const ok: string[] = [];
  let rejected = 0;
  for (const m of res.m ?? []) {
    const f = m.e?.find((e) => e.t === 'f');
    if (f?.a && senderMatches(r, f.a, f.p ?? f.d ?? '')) ok.push(m.id);
    else rejected++;
  }
  return { ids: ok, rejected, more: !!res.more };
}

/** How many messages a rule matches in its folders — one search, capped at 1,000. */
export async function countMatches(client: ZimbraClient, r: CleanRule): Promise<{ n: number; capped: boolean }> {
  if (ruleIsEmpty(r) || ruleFolders(r).length === 0) return { n: 0, capped: false };
  const page = await ruleIds(client, r, ruleQuery(r), COUNT_CAP);
  return { n: page.ids.length, capped: page.more };
}
/** Count for a raw query (e.g. several rules ORed together), capped at 1,000. */
export async function countQuery(client: ZimbraClient, query: string): Promise<{ n: number; capped: boolean }> {
  const page = await ids(client, query, COUNT_CAP);
  return { n: page.ids.length, capped: page.more };
}

export interface PreviewMsg {
  id: string;
  subject: string;
  from: string;
  date?: number;
}
/** A few newest matches, to show what a rule will move. */
export async function previewMatches(client: ZimbraClient, r: CleanRule, limit = 4): Promise<PreviewMsg[]> {
  if (ruleIsEmpty(r) || ruleFolders(r).length === 0) return [];
  // over-fetch when hits get the sender check, so a few rejects don't empty the preview
  const fetch = ruleHasDomainSenders(r) ? limit * 5 : limit;
  const res = (await call(client, () => client.mail.search({ query: ruleQuery(r), types: 'message', limit: fetch, sortBy: 'dateDesc' }))) as {
    m?: SearchMsg[];
  };
  const hits = (res.m ?? []).filter((m) => {
    if (!ruleHasDomainSenders(r)) return true;
    const f = m.e?.find((e) => e.t === 'f');
    return !!f?.a && senderMatches(r, f.a, f.p ?? f.d ?? '');
  });
  return hits.slice(0, limit).map((m) => {
    const f = m.e?.find((e) => e.t === 'f');
    return { id: m.id, subject: m.su || '(no subject)', from: f?.p ?? f?.d ?? f?.a ?? '', date: m.d };
  });
}

/* ------------------------------------------------------------------ */
/* Folders: sources and HouseCleaning/<source path> targets            */
/* ------------------------------------------------------------------ */

export interface SourceFolder {
  id: string;
  /** "Inbox", "ap@mitral.dk", "Projects/Old" */
  path: string;
  depth: number;
  /** a mounted share from another account (e.g. ap@mitral.dk) */
  shared?: boolean;
  /** share only: "<owner zid>:<owner folder id>" — how the owner's mail names its folder */
  remote?: string;
}

const isMailView = (v?: string) => !v || v === 'message' || v === 'conversation';

/**
 * Mail folders a rule may clean, in tree order (not the HouseCleaning tree,
 * Trash, Junk or Drafts). Mounted shares count only when the tree was loaded
 * with mountpoints traversed (so `perm` is known) and the share lets us delete
 * — moving out of a share is copy here + delete there (measured 2026-10-15 on
 * ap@mitral.dk). Folders inside a share are not offered.
 */
export function sourceFolders(root: Folder | undefined): SourceFolder[] {
  const out: SourceFolder[] = [];
  const walk = (f: Folder, parent: string, depth: number) => {
    for (const c of f.folder ?? []) {
      const name = c.name ?? '';
      const path = (c.absFolderPath ?? `${parent}/${name}`).replace(/^\/+/, '');
      if (depth === 0 && name === CLEAN_FOLDER) continue; // never clean the cleaned mail
      if (NEVER_SOURCE.has(c.id)) continue;
      if (isMailView(c.view)) out.push({ id: c.id, path, depth });
      walk(c, `/${path}`, depth + 1);
    }
    for (const l of f.link ?? []) {
      if (!isMailView(l.view) || l.broken || !l.perm?.includes('d')) continue;
      const path = (l.absFolderPath ?? `${parent}/${l.name ?? ''}`).replace(/^\/+/, '');
      out.push({ id: l.id, path, depth, shared: true, remote: l.zid && l.rid ? `${l.zid}:${l.rid}` : undefined });
    }
  };
  if (root) walk(root, '', 0);
  return out;
}

/** Folder tree with mountpoints traversed, so shares carry their rights (`perm`). */
export async function loadFolderRoot(client: ZimbraClient): Promise<Folder | undefined> {
  const res = await call(client, () => client.mail.getFolder({ traverseMountpoints: true }));
  return res.folder?.[0];
}

/** "Inbox" → ["HouseCleaning", "Inbox"]; "ap@mitral.dk/Inbox" → ["HouseCleaning", "ap@mitral.dk", "Inbox"] */
export function targetPath(sourcePath: string): string[] {
  return [CLEAN_FOLDER, ...sourcePath.split('/').filter(Boolean)];
}

/** Id of HouseCleaning/<source path>, creating each missing level under the mailbox root. */
export async function ensureTarget(client: ZimbraClient, sourcePath: string): Promise<string> {
  let parent = '1';
  for (const name of targetPath(sourcePath)) {
    const res = await call(client, () => client.mail.createFolder({ name, parentFolderId: parent, view: 'message', fetchIfExists: true }));
    const id = res.folder?.[0]?.id;
    if (!id) throw new Error(`Could not create the folder ${targetPath(sourcePath).join('/')}`);
    parent = String(id);
  }
  return parent;
}

/** Current source folders of the mailbox, by id. */
export async function loadSourceFolders(client: ZimbraClient): Promise<Map<string, SourceFolder>> {
  return new Map(sourceFolders(await loadFolderRoot(client)).map((f) => [f.id, f]));
}

/* ------------------------------------------------------------------ */
/* Drag a mail from the list onto the panel → a rule suggestion        */
/* ------------------------------------------------------------------ */

/** dataTransfer type of a mail dragged from the message list */
export const MAIL_DRAG_TYPE = 'application/x-zwc-mail';

export interface DraggedMail {
  /** sender address */
  from: string;
  /** sender display name */
  name?: string;
  subject?: string;
  /** folder the mail is in: our folder id, or "<owner zid>:<owner folder id>" for a mail in a share */
  folderId?: string;
}

/**
 * The folder of a mail from a search hit. A mail in a share reports the
 * OWNER's folder id as `l` (ap@mitral.dk Inbox → l="2", live 2026-10-15),
 * which collides with our own ids; its id carries the owner ("<zid>:<id>"),
 * so the pair names the share's folder. Prefer the folder being viewed when
 * there is one.
 */
export function hitFolder(id: string, l: string | undefined): string | undefined {
  if (!l) return undefined;
  const i = id.lastIndexOf(':');
  return i > 0 && !l.includes(':') ? `${id.slice(0, i)}:${l}` : l;
}

/** Map a mail's folder to a folder a rule can clean (a share's "<zid>:<rid>" → its mount id). */
export function cleanableFolder(sources: SourceFolder[], folderId: string | undefined): string | undefined {
  if (!folderId) return undefined;
  return sources.find((f) => f.id === folderId || f.remote === folderId)?.id;
}

/**
 * What a dragged mail suggests as a sender: its base domain ("news.paypal.dk"
 * → "paypal.dk"), or the full address for a personal mailbox (gmail, …) or
 * our own domain, where a domain would catch far too much.
 */
export function senderFromMail(addr: string, ownDomain: string): string {
  const a = addr.trim().toLowerCase();
  const at = a.lastIndexOf('@');
  if (at < 0) return a;
  const base = baseDomain(a.slice(at + 1));
  const brand = brandOf(base);
  if (FREEMAIL.has(brand) || base === baseDomain(ownDomain)) return a;
  return base;
}

/** "Re: SV: Fwd: Your order 123" → "Your order 123" */
export function cleanSubject(su: string): string {
  return su.replace(/^\s*((re|sv|fw|fwd|vs|aw|wg)\s*:\s*)+/i, '').trim();
}

/** A new rule for a dragged mail: its sender, from the folder it is in (when that folder can be cleaned). */
export function ruleFromMail(d: DraggedMail, ownDomain: string, sources: SourceFolder[]): CleanRule {
  const sender = senderFromMail(d.from, ownDomain);
  return newRule({
    name: `All ${d.name?.trim() || sender}`,
    senders: [sender],
    folders: [cleanableFolder(sources, d.folderId) ?? INBOX_ID],
    logo: d.from.toLowerCase(),
  });
}

/** The dragged mail, if this drag event carries one. */
export function readDraggedMail(dt: DataTransfer | null): DraggedMail | null {
  const raw = dt?.getData(MAIL_DRAG_TYPE);
  if (!raw) return null;
  try {
    const d = JSON.parse(raw) as DraggedMail;
    return typeof d.from === 'string' && d.from.includes('@') ? d : null;
  } catch {
    return null;
  }
}

/**
 * Move everything a rule matches into HouseCleaning/<source folder>, one source
 * folder at a time, in batches. Moved mail leaves the source, so every batch
 * re-queries from offset 0. A source folder that no longer exists is skipped.
 * `target` maps a source folder to its target folder id (callers cache it).
 */
export async function runRule(
  client: ZimbraClient,
  r: CleanRule,
  sources: Map<string, SourceFolder>,
  target: (src: SourceFolder) => Promise<string>,
  onProgress?: (moved: number) => void,
  signal?: { cancelled: boolean },
): Promise<number> {
  if (ruleIsEmpty(r)) return 0;
  let moved = 0;
  for (const srcId of ruleFolders(r)) {
    const src = sources.get(srcId);
    if (!src) continue;
    const query = ruleQuery(r, srcId);
    const size = src.shared ? BATCH_SHARED : BATCH;
    let folderId: string | undefined;
    let gatewayFails = 0;
    let skip = 0; // hits that failed the sender check stay put — page past them
    for (let i = 0; i < Math.ceil(MAX_MOVE / size); i++) {
      if (signal?.cancelled) return moved;
      const page = await ruleIds(client, r, query, size, skip);
      skip += page.rejected;
      if (page.ids.length === 0) {
        if (page.more && page.rejected > 0) continue; // a page of rejects only — look further
        break;
      }
      folderId ??= await target(src); // only create the target when something moves
      try {
        await call(client, () => client.mail.msgAction({ ids: page.ids, op: 'move', folderId: folderId! }));
        gatewayFails = 0;
        moved += page.ids.length;
        onProgress?.(moved);
      } catch (e) {
        if (!isGatewayTimeout(e) || ++gatewayFails > GATEWAY_RETRIES) throw e;
        await sleep(GATEWAY_PAUSE_MS); // the next query shows what actually moved
        continue;
      }
      if (!page.more) break;
      await sleep(BATCH_PAUSE_MS); // let the rest of the app get its turn on the lock
    }
  }
  return moved;
}

/**
 * Run several rules one after another (the server-side clean job). Targets
 * HouseCleaning/<source path> are created once per run, on first use.
 * `onProgress(rule name, total moved)`.
 */
export async function runClean(
  client: ZimbraClient,
  rules: CleanRule[],
  onProgress?: (ruleName: string, moved: number) => void,
  signal?: { cancelled: boolean },
): Promise<number> {
  const todo = rules.filter((r) => !ruleIsEmpty(r));
  if (todo.length === 0) return 0;
  const sources = await loadSourceFolders(client);
  const targets = new Map<string, Promise<string>>();
  const target = (src: SourceFolder) => {
    let t = targets.get(src.id);
    if (!t) {
      t = ensureTarget(client, src.path);
      t.catch(() => targets.delete(src.id));
      targets.set(src.id, t);
    }
    return t;
  };
  let total = 0;
  for (const r of todo) {
    if (signal?.cancelled) break;
    const before = total;
    onProgress?.(r.name, total);
    total += await runRule(client, r, sources, target, (n) => onProgress?.(r.name, before + n), signal);
  }
  return total;
}

/** A clean job as the server reports it (GET /api/clean/status). */
export interface CleanJob {
  id: string;
  state: 'running' | 'done' | 'error' | 'cancelled';
  /** rule being run right now */
  ruleName: string;
  moved: number;
  auto: boolean;
  startedAt: number;
  finishedAt?: number;
  error?: string;
}

/* ------------------------------------------------------------------ */
/* Suggestions: frequent senders in the Inbox                          */
/* ------------------------------------------------------------------ */

const SECOND_LEVEL = new Set(['co', 'com', 'org', 'net', 'ac', 'gov', 'edu']);
/** personal mailbox providers — senders there are people, not a brand to clean */
const FREEMAIL = new Set(['gmail', 'googlemail', 'hotmail', 'outlook', 'live', 'msn', 'yahoo', 'icloud', 'me', 'mac', 'aol', 'proton', 'protonmail', 'gmx', 'mail']);
/** "mail.e.paypal.co.uk" → "paypal.co.uk" */
export function baseDomain(domain: string): string {
  const p = domain.toLowerCase().split('.').filter(Boolean);
  if (p.length <= 2) return p.join('.');
  const n = p.length;
  if (p[n - 1]!.length === 2 && SECOND_LEVEL.has(p[n - 2]!)) return p.slice(n - 3).join('.');
  return p.slice(n - 2).join('.');
}
/** "paypal.co.uk" → "paypal" — the brand word used for an "all of X" rule. */
export function brandOf(base: string): string {
  return base.split('.')[0] ?? base;
}

export interface SenderSuggestion {
  /** the brand word(s) a rule uses — several when one sender uses several domains */
  brands: string[];
  brand: string;
  domain: string;
  /** most common display name seen, e.g. "PayPal" */
  name: string;
  /** a sample sender address (for the logo) */
  sample: string;
  count: number;
}

/** Group the newest Inbox mail by sender brand; most frequent first. */
export async function senderSuggestions(client: ZimbraClient, ownDomain: string, sample = 1000): Promise<SenderSuggestion[]> {
  const res = (await call(client, () => client.mail.search({ query: 'in:inbox -is:flagged', types: 'message', limit: sample, sortBy: 'dateDesc' }))) as {
    m?: Array<{ e?: Array<{ t?: string; a?: string; p?: string }> }>;
  };
  const own = brandOf(baseDomain(ownDomain));
  const by = new Map<string, { domain: string; names: Map<string, number>; sample: string; count: number }>();
  for (const m of res.m ?? []) {
    const f = m.e?.find((e) => e.t === 'f');
    const addr = f?.a?.toLowerCase();
    const at = addr?.lastIndexOf('@') ?? -1;
    if (!addr || at < 0) continue;
    const base = baseDomain(addr.slice(at + 1));
    const brand = brandOf(base);
    if (!brand || brand === own || brand.length < 3 || FREEMAIL.has(brand)) continue;
    const cur = by.get(brand) ?? { domain: base, names: new Map(), sample: addr, count: 0 };
    cur.count++;
    if (f?.p) cur.names.set(f.p, (cur.names.get(f.p) ?? 0) + 1);
    by.set(brand, cur);
  }
  const list = [...by.entries()].map(([brand, v]) => ({
    brands: [brand],
    brand,
    domain: v.domain,
    name: [...v.names.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? v.domain,
    sample: v.sample,
    count: v.count,
  }));
  // one sender on several domains (nytimes.com + newyorktimes.com) → one suggestion
  const byName = new Map<string, SenderSuggestion>();
  for (const s of list.sort((a, b) => b.count - a.count)) {
    const key = s.name.trim().toLowerCase();
    const prev = byName.get(key);
    if (prev && key !== s.domain) {
      prev.brands.push(s.brand);
      prev.domain = `${prev.domain.replace(/ \+\d+$/, '')} +${prev.brands.length - 1}`;
      prev.count += s.count;
    } else {
      byName.set(key, s);
    }
  }
  return [...byName.values()].sort((a, b) => b.count - a.count);
}
